import React, { useMemo } from "react";
import { Board } from "app/containers/Features/containers/Boards/types";
import {
  dateCreatedFormatter,
  dateFormatter,
  formatDateDistance,
} from "utils/helpers";
import Options from "app/containers/Features/containers/Boards/containers/Home/components/Options";
import CustomTooltip from "app/components/Tooltip";
import EditableRowItem from "app/containers/Features/containers/Boards/containers/Home/components/EditableRowItem";
import ProfileAvatar from "app/components/ProfileAvatar";
import { getMembersByIds } from "app/containers/Features/components/MemberSelection/helper";
import { useSelector } from "react-redux";
import { selectProjectMemberList } from "app/containers/Features/components/MemberSelection/redux/selector";

interface RowProps {
  isEditing: boolean;
  board: Board;
  onArchive: () => void;
  onDuplicate?: () => void;
  onFavorite?: () => void;
  handleBoardRename: () => void;
  setEditingBoardId: (boardId: string | null) => void;
  handleCopyBoardLink: () => void;
  handleFavoriteBoard: () => void;
  handleDuplicateBoard: () => void;
  handleRedirectToBoard: () => void;
}

export default function Row({
  board,
  onArchive,
  handleBoardRename,
  handleCopyBoardLink,
  isEditing,
  setEditingBoardId,
  handleFavoriteBoard,
  handleDuplicateBoard,
  handleRedirectToBoard,
}: RowProps) {
  const memberList = useSelector(selectProjectMemberList);
  const owner = useMemo(() => {
    return getMembersByIds(memberList, board.owner._id);
  }, [memberList, board.owner._id])[0];
  return (
    <tr
      className={`cursor-pointer border-b border-[#ECEEF2] h-[52px] group ${
        isEditing ? "" : " group "
      }`}
      onDoubleClick={() => !isEditing && handleRedirectToBoard()}
    >
      <td className=" text-left text-sm leading-[21px] font-normal text-[#333E4D]">
        <Options
          onArchive={onArchive}
          handleBoardRename={handleBoardRename}
          handleCopyBoardLink={handleCopyBoardLink}
          handleFavoriteBoard={handleFavoriteBoard}
          handleDuplicateBoard={handleDuplicateBoard}
        />
      </td>
      <td className="px-4 pr-2 py-[12px] text-left text-sm leading-[21px] font-normal text-[#333E4D] flex items-center flex-row min-w-[300px] bg-white group-hover:bg-[#ECEEF2]">
        <EditableRowItem
          board={board}
          handleBoardRename={handleBoardRename}
          handleCopyBoardLink={handleCopyBoardLink}
          isEditing={isEditing}
          setEditingBoardId={setEditingBoardId}
          handleFavoriteBoard={handleFavoriteBoard}
        />
      </td>
      <td className="px-4 pr-2 py-[12px] text-left text-sm leading-[21px] font-normal text-[#333E4D] bg-white group-hover:bg-[#ECEEF2]">
        <CustomTooltip text={dateFormatter(board?.lastViewed)}>
          <span>{formatDateDistance(board?.lastViewed)}</span>
        </CustomTooltip>
      </td>
      <td className="px-4 pr-2 py-[12px] text-left text-sm leading-[21px] font-normal text-[#333E4D] bg-white group-hover:bg-[#ECEEF2]">
        <CustomTooltip text={dateFormatter(board?.lastUpdated)}>
          <span>{formatDateDistance(board?.lastUpdated)}</span>
        </CustomTooltip>
      </td>
      <td className="px-4 pr-2 py-[12px] text-left text-sm leading-[21px] font-normal text-[#333E4D] bg-white group-hover:bg-[#ECEEF2]">
        <div className="flex items-center gap-2">
          <ProfileAvatar name={owner?.name} avatar={owner?.avatar} />
          <span className=" text-sm font-normal leading-[21px] truncate">
            {owner?.name}
          </span>
        </div>
      </td>
      <td className="px-4 pr-2 py-[12px] text-left text-sm leading-[21px] font-normal text-[#333E4D] bg-white group-hover:bg-[#ECEEF2]">
        <CustomTooltip text={dateFormatter(board?.createdAt)}>
          <span>{dateCreatedFormatter(board?.createdAt)}</span>
        </CustomTooltip>
      </td>
    </tr>
  );
}
